import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export const useAdvancedScrollAnimation = (options = {}) => {
  const {
    fadeOutOnLeave = true,
    mobileBreakpoint = 768,
    fadeThreshold = 0.3
  } = options;

  const sectionRef = useRef(null);
  const triggerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check screen size
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < mobileBreakpoint);
    };
    
    // Initial check
    checkScreenSize();
    
    // Add resize listener
    window.addEventListener('resize', checkScreenSize);

    // Clean up resize listener
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, [mobileBreakpoint]);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    
    const section = sectionRef.current;
    
    // If no trigger is provided, use the section itself
    const trigger = triggerRef.current || section;

    // Disable on mobile if needed
    if (isMobile) return;

    const scrollTrigger = ScrollTrigger.create({
      trigger: trigger,
      start: "top 80%", 
      end: "bottom 20%", 
      toggleActions: fadeOutOnLeave 
        ? "play reverse play reverse" 
        : "play none none reverse",
      onEnter: () => {
        gsap.to(section, { 
          opacity: 1, 
          y: 0, 
          duration: 0.5,
          ease: "power1.out",
          immediateRender: true
        });
      },
      onLeave: () => {
        if (fadeOutOnLeave) {
          gsap.to(section, { 
            opacity: fadeThreshold, 
            y: 50, 
            duration: 0.5,
            ease: "power1.in"
          });
        }
      },
      onEnterBack: () => {
        gsap.to(section, { 
          opacity: 1, 
          y: 0, 
          duration: 0.5,
          ease: "power1.out",
          immediateRender: true
        });
      },
      onLeaveBack: () => {
        if (fadeOutOnLeave) {
          gsap.to(section, { 
            opacity: fadeThreshold, 
            y: -50, 
            duration: 0.5,
            ease: "power1.in"
          });
        }
      }
    });

    return () => {
      scrollTrigger.kill();
    };
  }, [fadeOutOnLeave, fadeThreshold, isMobile]);

  return { 
    sectionRef, 
    triggerRef,
    isMobile 
  };
};